/**
 * The dashboard Overview model (spec 2026-10-09 §2.2 tab 1, §7.1; W4 card C1): six read-only cards (Ship,
 * Obligations, Active job, Alerts, Where, Standing) and the shell header's location, credits and hull, built from a
 * plain `OverviewInput` that `source.ts` reads from the game. Pure and DOM-free; it runs at the shell's refresh rate
 * (4 Hz) only while the dashboard is open, never per frame. Every word comes from `overview.json`.
 */
import { z } from 'zod'
import raw from './overview.json'
import { formatCr, formatDuration, formatDueIn, formatPct, formatStanding, formatTonnes } from '../../../format'
import type { FleetAlert } from '../../../../strategic/types'
import type { NearestStation } from '../../parts'
import { DASH_TABS, type DashTarget } from '../../registry'
import type { CourseTarget } from '../jobs/model'

const text = z.string().min(1)
const link = z.strictObject({ tab: z.enum(DASH_TABS), section: text.optional() })
const card = { title: text, link, linkLabel: text }

export const overviewConfigSchema = z.strictObject({
  comment: z.string(),
  header: z.strictObject({ location: text, near: text, outside: text, hull: text }),
  ship: z.strictObject({
    ...card,
    bars: z.strictObject({ shield: text, hull: text, energy: text }),
    torpedoes: text,
    tool: text,
    tools: z.array(text).length(3),
    cargo: text,
    cargoValue: text,
    destroyed: text,
    none: text,
  }),
  obligations: z.strictObject({
    ...card,
    nextDue: text,
    amount: text,
    collections: text,
    collectionsStates: z.strictObject({ none: text, notice: text, repo: text }),
    arrears: text,
    principal: text,
    debt: text,
    owned: text,
    pay: text,
    payLease: text,
    none: text,
  }),
  job: z.strictObject({ ...card, deadline: text, reward: text, expired: text, ready: text, more: text, story: text, none: text }),
  alerts: z.strictObject({
    ...card,
    jumpTo: text,
    sendWing: text,
    none: text,
    unread: z.array(z.strictObject({ label: text, section: text })),
  }),
  where: z.strictObject({
    ...card,
    system: text,
    near: text,
    nearValue: text,
    law: text,
    route: text,
    routeValue: text,
    jumps: z.strictObject({ one: text, many: text }),
    next: text,
    nextValue: text,
    noRoute: text,
    none: text,
  }),
  standing: z.strictObject({ ...card, row: text, none: text }),
})

export type OverviewConfig = z.infer<typeof overviewConfigSchema>

let cached: OverviewConfig | null = null

export function loadOverviewConfig(): OverviewConfig {
  cached ??= overviewConfigSchema.parse(raw)
  return cached
}

// ---------------------------------------------------------------------------------------------
// Input (what source.ts reads; plain data)
// ---------------------------------------------------------------------------------------------

export interface ShipInput {
  readonly alive: boolean
  /** 0..1 of the hull's maximum. */
  readonly hull: number
  readonly shield: number
  readonly energy: number
  readonly torpedoes: number
  /** `TOOL` code: 0 combat, 1 mining, 2 salvage. */
  readonly tool: number
}

export interface CargoInput {
  readonly usedT: number
  readonly capT: number
}

export type CollectionsState = 'none' | 'notice' | 'repo'

export interface LeaseInput {
  readonly owned: boolean
  readonly payment: number
  /** Game seconds. */
  readonly nextDueAt: number
  readonly principal: number
  readonly collections: CollectionsState
  /** Missed payments plus late fees owed. */
  readonly arrears: number
  /** Interest-free insurer debt (after ownership). */
  readonly debt: number
}

export interface ContractInput {
  /** One line: "Deliver 12 t of Ice to Stillwater Base". */
  readonly requirement: string
  /** The issuing (hand-in) station's name. */
  readonly station: string
  readonly deadlineAt: number
  readonly reward: number
  readonly ready: boolean
  /** Set course for the job (`jobs/model courseFor`: the work system, then the hand-in station), or null. */
  readonly course: CourseTarget | null
}

/** A docked-only action in flight (C0): the reason, the nearest known station offering it and its directory id. */
export interface DockedInput {
  readonly reason: string
  readonly nearest: NearestStation | null
  readonly stationId: string | null
}

export interface AlertInput {
  readonly text: string
  readonly tone: 'info' | 'good' | 'warn' | 'bad'
  readonly jump: boolean
  readonly wing: boolean
}

export interface RouteInput {
  readonly dest: string
  /** Systems left to cross. */
  readonly jumps: number
  /** The system the next relay leads to (null off the route's relay). */
  readonly next: string | null
  /** Metres to the next relay. */
  readonly nextM: number
}

export interface WhereInput {
  readonly system: string
  /** The nearest known station (this system first, else the fewest known jumps away), or null (none known). */
  readonly station: string | null
  /** Metres to it when in this system. */
  readonly stationM: number
  /** Known-route jumps to it (0 = this system). */
  readonly stationJumps: number
  /** The jurisdiction chip ("Lawless", "Tethys claim · tariff 4%"), or null before the strategic view arrives. */
  readonly law: string | null
  readonly lawTone: 'lawful' | 'lawless' | 'wanted'
  readonly route: RouteInput | null
}

export interface StandingInput {
  readonly faction: string
  readonly delta: number
  readonly reason: string
}

export interface OverviewInput {
  /** Game seconds (the career clock). */
  readonly now: number
  readonly ship: ShipInput | null
  readonly cargo: CargoInput | null
  readonly wallet: number | null
  readonly lease: LeaseInput | null
  /** Where to pay the lease (the nearest known Lease Office), or null outside the Reach. */
  readonly payAt: DockedInput | null
  /** The job the card speaks for (earliest deadline first), or null. */
  readonly contract: ContractInput | null
  /** Active contracts in all. */
  readonly contracts: number
  /** The story objective line, or null. */
  readonly story: string | null
  /** Newest first. */
  readonly alerts: readonly AlertInput[]
  readonly where: WhereInput | null
  /** Newest first. */
  readonly standing: readonly StandingInput[]
}

/** No data (no ship, no career, outside the Reach): every card shows its empty line. */
export const EMPTY_OVERVIEW_INPUT: OverviewInput = Object.freeze({
  now: 0,
  ship: null,
  cargo: null,
  wallet: null,
  lease: null,
  payAt: null,
  contract: null,
  contracts: 0,
  story: null,
  alerts: [],
  where: null,
  standing: [],
})

// ---------------------------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------------------------

export type OvTone = 'ok' | 'warn' | 'alert' | 'dim'

export interface OvLine {
  readonly label?: string
  readonly text: string
  readonly tone?: OvTone
  /** Alerts: the alert index in `OverviewInput.alerts` and the actions it offers. */
  readonly alert?: { readonly index: number; readonly jump: boolean; readonly wing: boolean }
}

export interface OvBar {
  readonly id: 'shield' | 'hull' | 'energy'
  readonly label: string
  /** 0..100. */
  readonly pct: number
}

export type OvCardId = 'ship' | 'obligations' | 'job' | 'alerts' | 'where' | 'standing'

export interface OvCard {
  readonly id: OvCardId
  readonly title: string
  readonly link: DashTarget
  readonly linkLabel: string
  readonly bars: readonly OvBar[]
  readonly lines: readonly OvLine[]
  /** The card's empty line (no data), or null. */
  readonly empty: string | null
  /** Obligations: the Pay `DockedAction` (label, reason, nearest station and its Set course target). */
  readonly docked?: OvDocked
  /** Active job: the Set course target. */
  readonly course?: CourseTarget
}

export interface OvDocked extends DockedInput {
  readonly label: string
}

export interface OvHeader {
  readonly location: string | null
  readonly credits: string | null
  readonly hull: string | null
  readonly hullTone: OvTone
}

export interface OverviewView {
  readonly header: OvHeader
  readonly cards: readonly OvCard[]
}

/** Cards show at most this many alerts and standing changes (spec: "the last 3"). */
export const OVERVIEW_ROWS = 3

/** Below this hull fraction the hull reads as an alert, below `WARN_HULL` as a warning. */
const ALERT_HULL = 0.25
const WARN_HULL = 0.5

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

function pct(fraction: number): number {
  return Math.round(Math.min(1, Math.max(0, fraction)) * 100)
}

function hullTone(f: number): OvTone {
  return f < ALERT_HULL ? 'alert' : f < WARN_HULL ? 'warn' : 'ok'
}

function target(l: OverviewConfig['ship']['link']): DashTarget {
  return l.section === undefined ? { tab: l.tab } : { tab: l.tab, section: l.section }
}

function base(id: OvCardId, c: { title: string; link: OverviewConfig['ship']['link']; linkLabel: string }): Omit<OvCard, 'bars' | 'lines' | 'empty'> {
  return { id, title: c.title, link: target(c.link), linkLabel: c.linkLabel }
}

export function shipCard(input: OverviewInput, cfg: OverviewConfig): OvCard {
  const c = cfg.ship
  const head = base('ship', c)
  const s = input.ship
  if (s === null) return { ...head, bars: [], lines: [], empty: c.none }
  const lines: OvLine[] = []
  if (!s.alive) lines.push({ text: c.destroyed, tone: 'alert' })
  lines.push({ label: c.torpedoes, text: String(s.torpedoes), tone: s.torpedoes === 0 ? 'dim' : undefined })
  lines.push({ label: c.tool, text: c.tools[s.tool] ?? c.tools[0] })
  if (input.cargo !== null) {
    const { usedT, capT } = input.cargo
    lines.push({
      label: c.cargo,
      text: fill(c.cargoValue, { used: formatTonnes(usedT), cap: formatTonnes(capT) }),
      tone: capT > 0 && usedT >= capT ? 'warn' : undefined,
    })
  }
  const bars: OvBar[] = s.alive
    ? [
        { id: 'shield', label: c.bars.shield, pct: pct(s.shield) },
        { id: 'hull', label: c.bars.hull, pct: pct(s.hull) },
        { id: 'energy', label: c.bars.energy, pct: pct(s.energy) },
      ]
    : []
  return { ...head, bars, lines, empty: null }
}

export function obligationsCard(input: OverviewInput, cfg: OverviewConfig): OvCard {
  const c = cfg.obligations
  const head = base('obligations', c)
  const l = input.lease
  if (l === null) return { ...head, bars: [], lines: [], empty: c.none }
  const lines: OvLine[] = []
  if (l.owned) {
    lines.push({ text: c.owned, tone: 'ok' })
    if (l.debt > 0) lines.push({ label: c.debt, text: formatCr(l.debt), tone: 'warn' })
    return { ...head, bars: [], lines, empty: null }
  }
  const overdue = l.nextDueAt <= input.now
  lines.push({ label: c.nextDue, text: formatDueIn(l.nextDueAt, input.now), tone: overdue ? 'alert' : undefined })
  lines.push({ label: c.amount, text: formatCr(l.payment) })
  lines.push({
    label: c.collections,
    text: c.collectionsStates[l.collections],
    tone: l.collections === 'repo' ? 'alert' : l.collections === 'notice' ? 'warn' : 'ok',
  })
  if (l.arrears > 0) lines.push({ label: c.arrears, text: formatCr(l.arrears), tone: 'warn' })
  lines.push({ label: c.principal, text: formatCr(l.principal) })
  if (l.debt > 0) lines.push({ label: c.debt, text: formatCr(l.debt), tone: 'warn' })
  // Paying is docked-only: a disabled Pay with the nearest known Lease Office and Set course (C0).
  if (input.payAt !== null) return { ...head, bars: [], lines, empty: null, docked: { label: c.payLease, ...input.payAt } }
  lines.push({ text: c.pay, tone: 'dim' })
  return { ...head, bars: [], lines, empty: null }
}

export function jobCard(input: OverviewInput, cfg: OverviewConfig): OvCard {
  const c = cfg.job
  const head = base('job', c)
  const lines: OvLine[] = []
  const k = input.contract
  if (k !== null) {
    lines.push({ text: k.requirement })
    const left = k.deadlineAt - input.now
    lines.push({ label: c.deadline, text: left > 0 ? formatDuration(left) : c.expired, tone: left > 0 ? undefined : 'alert' })
    lines.push({ label: c.reward, text: formatCr(k.reward) })
    if (k.ready) lines.push({ text: fill(c.ready, { station: k.station }), tone: 'ok' })
    if (input.contracts > 1) lines.push({ text: fill(c.more, { n: String(input.contracts - 1) }), tone: 'dim' })
  }
  if (input.story !== null) lines.push({ label: c.story, text: input.story })
  const empty = lines.length === 0 ? c.none : null
  return k !== null && k.course !== null ? { ...head, bars: [], lines, empty, course: k.course } : { ...head, bars: [], lines, empty }
}

const ALERT_TONE: Readonly<Record<AlertInput['tone'], OvTone | undefined>> = { info: undefined, good: 'ok', warn: 'warn', bad: 'alert' }

export function alertsCard(input: OverviewInput, cfg: OverviewConfig): OvCard {
  const c = cfg.alerts
  const head = base('alerts', c)
  const lines: OvLine[] = []
  const n = Math.min(OVERVIEW_ROWS, input.alerts.length)
  for (let i = 0; i < n; i++) {
    const a = input.alerts[i]
    lines.push({ text: a.text, tone: ALERT_TONE[a.tone], alert: { index: i, jump: a.jump, wing: a.wing } })
  }
  return { ...head, bars: [], lines, empty: n === 0 ? c.none : null }
}

/** The unread counts under the alerts ("Wire 12", "Feed 4", "Log 1"); the shell's section badges, zeros dropped. */
export function unreadChips(badges: Readonly<Record<string, number>>, cfg: OverviewConfig): { label: string; count: number; section: string }[] {
  const out: { label: string; count: number; section: string }[] = []
  for (const u of cfg.alerts.unread) {
    const count = badges[u.section] ?? 0
    if (count > 0) out.push({ label: u.label, count, section: u.section })
  }
  return out
}

/** A distance in metres: "850 m", "12.3 km", "4.56 Mm", "38.2 Gm". */
export function formatRange(m: number): string {
  const a = Math.max(0, m)
  if (a < 1000) return `${Math.round(a)} m`
  if (a < 1e6) return `${(a / 1e3).toFixed(1)} km`
  if (a < 1e9) return `${(a / 1e6).toFixed(2)} Mm`
  return `${(a / 1e9).toFixed(1)} Gm`
}

function jumpsText(n: number, c: OverviewConfig['where']): string {
  return n === 1 ? c.jumps.one : fill(c.jumps.many, { n: String(n) })
}

export function whereCard(input: OverviewInput, cfg: OverviewConfig): OvCard {
  const c = cfg.where
  const head = base('where', c)
  const w = input.where
  if (w === null) return { ...head, bars: [], lines: [], empty: c.none }
  const lines: OvLine[] = [{ label: c.system, text: w.system }]
  if (w.station !== null) {
    const dist = w.stationJumps === 0 ? formatRange(w.stationM) : jumpsText(w.stationJumps, c)
    lines.push({ label: c.near, text: fill(c.nearValue, { station: w.station, dist }) })
  }
  if (w.law !== null) lines.push({ label: c.law, text: w.law, tone: w.lawTone === 'wanted' ? 'alert' : w.lawTone === 'lawless' ? 'warn' : 'ok' })
  const r = w.route
  if (r === null) lines.push({ label: c.route, text: c.noRoute, tone: 'dim' })
  else {
    lines.push({ label: c.route, text: fill(c.routeValue, { dest: r.dest, jumps: jumpsText(r.jumps, c) }) })
    if (r.next !== null) lines.push({ label: c.next, text: fill(c.nextValue, { system: r.next, dist: formatRange(r.nextM) }) })
  }
  return { ...head, bars: [], lines, empty: null }
}

export function standingCard(input: OverviewInput, cfg: OverviewConfig): OvCard {
  const c = cfg.standing
  const head = base('standing', c)
  const lines: OvLine[] = []
  const n = Math.min(OVERVIEW_ROWS, input.standing.length)
  for (let i = 0; i < n; i++) {
    const s = input.standing[i]
    const arrow = s.delta > 0 ? ' ▲' : s.delta < 0 ? ' ▼' : ''
    lines.push({
      label: s.faction,
      text: fill(c.row, { delta: `${formatStanding(s.delta)}${arrow}`, reason: s.reason }),
      tone: s.delta > 0 ? 'ok' : s.delta < 0 ? 'warn' : undefined,
    })
  }
  return { ...head, bars: [], lines, empty: n === 0 ? c.none : null }
}

export function overviewHeader(input: OverviewInput, cfg: OverviewConfig): OvHeader {
  const h = cfg.header
  const w = input.where
  const location = w === null ? h.outside : w.station !== null && w.stationJumps === 0 ? fill(h.near, { system: w.system, station: w.station }) : fill(h.location, { system: w.system })
  const s = input.ship
  return {
    location,
    credits: input.wallet === null ? null : formatCr(input.wallet),
    hull: s === null ? null : fill(h.hull, { pct: s.alive ? formatPct(s.hull) : formatPct(0) }),
    hullTone: s === null ? 'dim' : hullTone(s.alive ? s.hull : 0),
  }
}

/** The whole Overview: the header and the six cards in the spec's order. */
export function overviewModel(input: OverviewInput, cfg: OverviewConfig = loadOverviewConfig()): OverviewView {
  return {
    header: overviewHeader(input, cfg),
    cards: [shipCard(input, cfg), obligationsCard(input, cfg), jobCard(input, cfg), alertsCard(input, cfg), whereCard(input, cfg), standingCard(input, cfg)],
  }
}

/** What the shell carries in its snapshot (the dashboard feature polls it at the refresh rate while open). */
export interface OverviewData {
  readonly view: OverviewView
  /** The alert objects behind the alerts card's rows (same order), for Jump to / Send wing. */
  readonly alerts: readonly FleetAlert[]
  /** Change key for the shell's re-render test. */
  readonly key: string
  /** Set course (the Obligations Pay station, the Active job); absent without a Reach. */
  readonly course?: (target: CourseTarget) => void
}

/** One snapshot from an input and its alerts. */
export function overviewData(input: OverviewInput, alerts: readonly FleetAlert[], cfg: OverviewConfig = loadOverviewConfig()): OverviewData {
  const view = overviewModel(input, cfg)
  return { view, alerts, key: overviewKey(view) }
}

/** A change key for the shell's re-render test (one string per 4 Hz poll while open). */
export function overviewKey(v: OverviewView): string {
  return JSON.stringify(v)
}
